import React from "react";
import { CheckCircle2Icon, MinusIcon, PlusIcon } from "lucide-react";
import { firm } from "../../data/site";
import { useCaseReviewForm } from "../../hooks/useCaseReviewForm";
import { actionClass } from "../../utils/actionStyles";
import { FormField } from "./FormField";
import { SiteLink } from "../SiteLink";
type CaseReviewFormProps = {
  showReviewNotes: boolean;
};
export function CaseReviewForm({
  showReviewNotes
}: CaseReviewFormProps) {
  const {
    values,
    errors,
    errorList,
    previewSubmitted,
    showOptional,
    setShowOptional,
    summaryRef,
    statusRef,
    handleChange,
    handleSubmit,
    reset
  } = useCaseReviewForm();
  if (previewSubmitted) {
    return <div ref={statusRef} tabIndex={-1} role="status" className="rounded-lg border-2 border-ink bg-white p-7 md:p-9" data-id="element-53" data-mp="19jjz1jypce9">
        <CheckCircle2Icon aria-hidden="true" className="h-8 w-8 text-ink" data-id="element-54" data-mp="590ptw3awbjy" />
        <h3 className="mt-4 text-h3 font-bold text-ink" data-id="element-55" data-mp="aa7uhma5cume">Preview only — nothing was sent</h3>
        <p className="mt-3 max-w-measure text-slate" data-id="element-56" data-mp="1qqbbl5k896z0">
          This design prototype does not transmit form information. In production, the firm’s approved confirmation
          message appears here.
        </p>
        <button type="button" onClick={reset} className={actionClass("outline", "mt-6")} data-id="element-57" data-mp="1hq68h7m2guqn">
          Reset the preview form
        </button>
      </div>;
  }
  return <form noValidate onSubmit={handleSubmit} aria-labelledby="case-review-form-title" className="rounded-lg border border-line bg-white p-6 md:p-9" data-id="element-58" data-mp="2abfwuagx8345">
      <h3 id="case-review-form-title" className="text-h3 font-bold text-ink" data-id="element-59" data-mp="20m61mlqs64bt">
        Request a free case review
      </h3>
      <p className="mt-2 text-meta text-slate" data-id="element-60" data-mp="1vnbkrpcdipuc">All fields are required unless marked optional.</p>

      {errorList.length > 0 && <div ref={summaryRef} tabIndex={-1} role="alert" aria-labelledby="form-error-title" className="mt-6 rounded-md border-2 border-crimson bg-white p-5" data-id="element-61" data-mp="2t51ytqu2skn1">
          <h4 id="form-error-title" className="text-label font-bold text-crimson" data-id="element-62" data-mp="qgbq4uhzwlqm">
            Please fix {errorList.length === 1 ? "this item" : `these ${errorList.length} items`}:
          </h4>
          <ul className="mt-2 list-disc space-y-1 pl-6 text-meta" data-id="element-63" data-mp="ho037im3bgfe">
            {errorList.map(item => <li key={item.name} data-id="element-64" data-mp="2qhdoe1l8z6w">
                <a href={`#cr-${item.name}`} className="font-semibold text-ink underline underline-offset-4" data-id="element-65" data-mp="2pfrbdf4gq9v">
                  {item.message}
                </a>
              </li>)}
          </ul>
        </div>}

      <div className="mt-6 grid gap-6 sm:grid-cols-2" data-id="element-66" data-mp="3py1l4d1cd0fv">
        <FormField id="cr-firstName" label="First name" autoComplete="given-name" value={values.firstName} onChange={handleChange("firstName")} error={errors.firstName} data-id="element-67" data-mp="3uvtfddpgkyn8" />
        <FormField id="cr-lastName" label="Last name" autoComplete="family-name" value={values.lastName} onChange={handleChange("lastName")} error={errors.lastName} data-id="element-68" data-mp="1t1c69ra0zv0i" />
        <FormField id="cr-phone" label="Phone number" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={handleChange("phone")} error={errors.phone} data-id="element-69" data-mp="2c072kqjrusp0" />
        <FormField id="cr-email" label="Email" type="email" autoComplete="email" value={values.email} onChange={handleChange("email")} error={errors.email} data-id="element-70" data-mp="2lzo5x1bjwuht" />
      </div>

      <div className="mt-6 border-t border-line pt-5" data-id="element-71" data-mp="2qxzfdaxxp2zi">
        <button type="button" aria-expanded={showOptional} aria-controls="cr-optional" onClick={() => setShowOptional(!showOptional)} className="inline-flex min-h-[48px] items-center gap-3 text-label font-bold text-ink hover:text-crimson" data-id="element-72" data-mp="2qv3izr1qimd6">
          <span className="flex h-7 w-7 items-center justify-center rounded border-2 border-ink" data-id="element-73" data-mp="y2f1obj23wcd">
            {showOptional ? <MinusIcon aria-hidden="true" className="h-4 w-4" data-id="element-74" data-mp="170ikh02nqf0h" /> : <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-75" data-mp="1bzldfm5d4us3" />}
          </span>
          Add details (optional)
        </button>
        {showOptional && <div id="cr-optional" className="mt-4 grid gap-6" data-id="element-76" data-mp="f7hwawgdc92w">
            <div className="sm:max-w-[14rem]" data-id="element-77" data-mp="jbzcklnis48x">
              <FormField id="cr-zip" label="ZIP code" optional inputMode="numeric" autoComplete="postal-code" value={values.zip} onChange={handleChange("zip")} error={errors.zip} data-id="element-78" data-mp="oa984xd5p6v2" />
            </div>
            <FormField id="cr-details" label="Crash details" optional multiline hint="A few sentences is enough." value={values.details} onChange={handleChange("details")} error={errors.details} data-id="element-79" data-mp="jmzb3vbvik0" />
          </div>}
      </div>

      <div className="mt-6 rounded-md bg-ivory p-4 text-meta text-slate" data-id="element-80" data-mp="3fa86qnnr6qeh">
        <p data-id="element-81" data-mp="3b6ioefxo951w">
          Read our{" "}
          <SiteLink path="/privacy-policy/" className="font-semibold text-ink underline underline-offset-4" data-id="element-82" data-mp="367fhsr21mqrt">Privacy Policy</SiteLink>
          . Prefer to talk?{" "}
          <a href={firm.phoneHref} className="font-semibold text-ink underline underline-offset-4" data-id="element-83" data-mp="31ew3frvj7rly">
            Call {firm.phoneDisplay}
          </a>
          .
        </p>
      </div>

      <button type="submit" className={actionClass("call", "mt-6 w-full")} data-id="element-84" data-mp="2rkdyykiajs13">
        Request a case review
      </button>
    </form>;
}